
import Vue from 'vue';
import { CvButton, CvCheckbox } from '@carbon/vue';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { mapState, mapActions } from 'pinia';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useHistoryStore } from '@/stores/history';
import { useStore as useShapeStore } from '@/stores/shape';
import FitToWidth16 from '@carbon/icons-vue/es/fit-to-width/16';
import { mm2px } from '@/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  AnnotationShape,
  LogoShape,
} from '@/web-components/import-wizard/typings/pintura';
import { createStateSnapshot } from '@/utils/historyHelper';
import { lineWidth } from '@/constants/areaLineWidth';

export default Vue.extend({
  name: 'LanyardPanel',
  components: {
    CvButton,
    FitToWidth16,
    CvCheckbox,
  },
  computed: {
    ...mapState(useMirrorStore, ['currentMirrorSettings']),
    ...mapState(useShapeStore, ['shapes']),
  },
  methods: {
    ...mapActions(useMirrorStore, ['updateMirror', 'updateRotation']),
    ...mapActions(useHistoryStore, ['recordHistory']),

    async onExecuteSpread() {
      var shapesStore = useShapeStore();
      var productStore = useProductStore();

      const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
        productStore.activePosition;
      const webImageWidthPx =
        (originalImageWidthPx &&
          originalImageWebDpi &&
          originalImageDpi &&
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi) ||
        1;

      const rectLineWidth = (lineWidth * webImageWidthPx) / 100;
      const currentShapes = [...this.shapes];
      var minimumDistance = mm2px(
        2,
        productStore.selectedPrintPosition.originalImageWebDpi
      );

      var printPositionArea = productStore.drawPoints(
        productStore.selectedPrintPosition.id
      );

      currentShapes.sort((a, b) => a.x - b.x);

      const updatedShapesPositions = [] as AnnotationShape[];
      currentShapes.forEach((shape, index) => {
        if (shape && index === 0) {
          updatedShapesPositions.push({
            ...shape,
            x: printPositionArea.x + rectLineWidth,
          });
        } else if (shape && index > 0) {
          if (
            shape.x -
              (currentShapes[index - 1].x + currentShapes[index - 1].width) >=
            0
          ) {
            updatedShapesPositions.push({
              ...shape,
              x:
                printPositionArea.x +
                rectLineWidth +
                (shape.x - currentShapes[0].x) +
                (shape.x -
                  (currentShapes[index - 1].x +
                    currentShapes[index - 1].width)),
            });
          } else {
            updatedShapesPositions.push({
              ...shape,
              x:
                printPositionArea.x +
                rectLineWidth +
                (shape.x - currentShapes[0].x),
            });
          }
        }
      });

      shapesStore.$patch((state) => {
        state.shapes = updatedShapesPositions;
      });

      var minimumX = 0;
      var maximumX = 0;
      for (
        var index = 0;
        index < shapesStore.positionShapeMap.length;
        index++
      ) {
        if (
          shapesStore.positionShapeMap[index].id ===
          productStore.selectedPrintPosition.id
        ) {
          const shape = {
            ...shapesStore.shapes?.find(
              (a) => a.id === shapesStore.positionShapeMap[index].logoId
            ),
          } as AnnotationShape;

          if (shape) {
            if (minimumX === 0) {
              minimumX = shape.x;
            } else {
              if (shape.x < minimumX) {
                minimumX = shape.x;
              }
            }

            if (maximumX === 0) {
              maximumX = shape.x + shape.width;
            } else {
              if (shape.x + shape.width > maximumX) {
                maximumX = shape.x + shape.width;
              }
            }
          }
        }
      }

      var widthOfAllShapesOnSelectedPosition = maximumX - minimumX;

      //TODO: change 2 to someting that represent print position lines
      var usedWidthOfSelectedPrintPosition = maximumX - printPositionArea.x + 2;
      var numberOfDuplicateActions = 0;
      var spreadFinished = false;

      while (spreadFinished === false) {
        if (
          usedWidthOfSelectedPrintPosition +
            minimumDistance +
            widthOfAllShapesOnSelectedPosition <=
          printPositionArea.w
        ) {
          numberOfDuplicateActions++;
          usedWidthOfSelectedPrintPosition =
            usedWidthOfSelectedPrintPosition +
            minimumDistance +
            widthOfAllShapesOnSelectedPosition;
        } else {
          if (numberOfDuplicateActions > 0) {
            var freeSpace =
              printPositionArea.w - usedWidthOfSelectedPrintPosition;
            minimumDistance =
              minimumDistance + freeSpace / numberOfDuplicateActions;
          }

          spreadFinished = true;
        }
      }

      const promises = [];

      for (
        var indexOfDuplication = 0;
        indexOfDuplication < numberOfDuplicateActions;
        indexOfDuplication++
      ) {
        for (var i = 0; i < shapesStore.positionShapeMap.length; i++) {
          if (
            shapesStore.positionShapeMap[i].id ===
            productStore.selectedPrintPosition.id
          ) {
            const shape = {
              ...shapesStore.shapes?.find(
                (a) => a.id === shapesStore.positionShapeMap[i].logoId
              ),
            } as AnnotationShape;

            if (shape) {
              var id = uuidv4();
              promises.push(
                shapesStore.addArtworkPayload(
                  {
                    ...shape,
                    id: id,
                    personalizationArtworkId: id,
                    x:
                      shape.x +
                      widthOfAllShapesOnSelectedPosition *
                        (indexOfDuplication + 1) +
                      minimumDistance * (indexOfDuplication + 1),
                  } as LogoShape,
                  productStore.selectedPrintPosition.id,
                  productStore.selectedPrintPosition.techniqueId as string,
                  false
                )
              );
            }
          }
        }
      }

      await Promise.all(promises).then(() => {
        if (this.currentMirrorSettings?.isMirrorApplied) {
          this.onMirrorAppliedChange(true);
        }
        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      });
    },

    onMirrorAppliedChange(checked: boolean) {
      this.updateMirror(checked);

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },

    onOppositeRotationChange(checked: boolean) {
      this.updateRotation(checked);

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },
  },
});
